import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
    ClientEntity,
    OrderController,
    OrderEntity,
    OrderStatusEnum,
    ScheduleEntity,
} from '@domain';
import { catchError, map, Observable, of, tap, throwError } from 'rxjs';
import { PaginatedEntity, PaginationParams } from 'src/app/domain/DTOs/paginate.entity';
import { FeedMainService } from './feed-main.service';

@Injectable({
    providedIn: 'root',
})
export class OrderMainService {
    public loading = signal<boolean>(false);
    public paginate = signal<PaginatedEntity<OrderEntity>>(null);
    public orders = signal<OrderEntity[]>([]);
    public currentOrder = signal<OrderEntity>(null);
    public loadingArchived = signal<boolean>(false);
    public archivedPaginate = signal<PaginatedEntity<OrderEntity>>(null);
    public archivedOrders = signal<OrderEntity[]>([]);
    public schedules = signal<ScheduleEntity[]>([]);
    public loadingSchedules = signal<boolean>(false);
    /** Incrementado para solicitar uma recarga da lista de documentos do serviço atual. */
    public documentsReloadTick = signal<number>(0);

    /** Sequência das buscas: só a resposta da última pedida entra na lista. */
    private _requestSeq = 0;
    private _archivedRequestSeq = 0;

    // * [Injects]
    private _orderController = inject(OrderController);
    private _router = inject(Router);
    private _feedMainService = inject(FeedMainService);

    /**
     * Busca os serviços
     * @param status Status do serviço
     * @param nameOrDescription Nome ou descrição do serviço
     */
    public getAllOrders(
        status: OrderStatusEnum = OrderStatusEnum.PROGRESS,
        nameOrDescription?: string,
        page?: number
    ): Observable<PaginatedEntity<OrderEntity>> {
        this.loading.set(true);
        const seq = ++this._requestSeq;

        const paginate: PaginationParams = {
            page: page ?? this.paginate()?.page ?? 1,
            limit: 10,
        };

        // Busca os serviços
        return this._orderController.getByFilter(status, paginate, nameOrDescription).pipe(
            map(paginate => {
                // Resposta de uma busca já substituída por outra (digitação rápida): descarta
                if (seq !== this._requestSeq) return paginate;

                // Seta os valores da paginação
                this.paginate.set(paginate);

                // Se for a primeira página ou não houver dados, substitui o valor da lista de serviços
                if (paginate.page == 1 || this.orders().length == 0) {
                    this.orders.set(paginate.data);
                }
                // Caso contrário, adiciona os novos serviços à lista
                else {
                    this.orders.update(orders => [...orders, ...paginate.data]);
                }

                // Verifica se há um orderId na URL e seta como atual se existir na lista
                this._checkIfOrderIdIsInUrl(paginate.page);

                // Desativa o loading
                this.loading.set(false);

                return paginate;
            }),
            catchError(() => {
                if (seq === this._requestSeq) this.loading.set(false);

                return of(null);
            })
        );
    }

    /**
     * Busca os serviços arquivados
     * @param nameOrDescription Nome ou descrição do serviço
     * @param page Página a ser buscada
     */
    public getArchivedOrders(
        nameOrDescription?: string,
        page?: number
    ): Observable<PaginatedEntity<OrderEntity>> {
        this.loadingArchived.set(true);
        const seq = ++this._archivedRequestSeq;

        const paginate: PaginationParams = {
            page: page ?? this.archivedPaginate()?.page ?? 1,
            limit: 10,
        };

        return this._orderController
            .getByFilter(OrderStatusEnum.FINISHED, paginate, nameOrDescription)
            .pipe(
                map(paginate => {
                    // Resposta de uma busca já substituída por outra: descarta
                    if (seq !== this._archivedRequestSeq) return paginate;

                    this.archivedPaginate.set(paginate);

                    if (paginate.page == 1 || this.archivedOrders().length == 0) {
                        this.archivedOrders.set(paginate.data);
                    } else {
                        this.archivedOrders.update(orders => [...orders, ...paginate.data]);
                    }

                    this.loadingArchived.set(false);

                    return paginate;
                }),
                catchError(() => {
                    if (seq === this._archivedRequestSeq) this.loadingArchived.set(false);

                    return of(null);
                })
            );
    }

    /**
     * Restaura um serviço arquivado, movendo-o de volta para a lista de serviços ativos
     * @param id ID do serviço
     */
    public restoreOrder(id: string): Observable<void> {
        return this._orderController.restore(id).pipe(
            tap(() => {
                const restored = this.archivedOrders().find(order => order._id === id);

                // Remove o serviço da lista de arquivados
                this.archivedOrders.update(orders => orders.filter(order => order._id !== id));

                // Adiciona o serviço de volta à lista de serviços ativos
                if (restored) {
                    this.orders.update(orders => [
                        { ...restored, status: OrderStatusEnum.PROGRESS },
                        ...orders,
                    ]);
                }

                // Restaurado volta a ser item ativo do feed
                this._feedMainService.refreshSilent();
            })
        );
    }

    /**
     * Verifica se o ID da order está na URL e seta como atual se existir
     * @param page Página que acabou de chegar
     */
    private _checkIfOrderIdIsInUrl(page: number): void {
        // "Carregar mais" não mexe na seleção: antes trocava o serviço aberto
        // pelo primeiro da página nova.
        if (page > 1 && this.currentOrder()) return;

        const orderIdFromUrl = this._router.routerState.snapshot.root.queryParams['orderId'];

        // Se tiver OrderId na URL, procura a order com o ID na lista inteira já carregada
        if (orderIdFromUrl) {
            const orderFromUrl = this.orders().find(order => order._id === orderIdFromUrl);

            // Se encontrar a order, seta como atual
            if (orderFromUrl) {
                this.currentOrder.set(orderFromUrl);

                return;
            }
        }

        // Se não encontrar a order, seta o primeiro serviço como ativo
        this.setCurrentOrder(this.orders()[0]);
    }

    /**
     * Limpa o serviço atual
     */
    public cleanCurrentOrder(): void {
        this.currentOrder.set(null);

        // Remove o OrderId da URL
        this._updateUrlWithOrderId(null);
    }

    /**
     * Seta o serviço atual
     * @param order Serviço
     */
    public setCurrentOrder = (order: OrderEntity) => {
        this.currentOrder.set(order);
        this._updateUrlWithOrderId(order?._id);
    };

    /**
     * Remove o serviço atual, e seta o próximo serviço da lista
     */
    public removeCurrentOrder(): void {
        // Remove o serviço atual da lista principal
        this.orders.update(orders =>
            orders.filter(order => order._id !== this.currentOrder()?._id)
        );

        // Seta o próximo serviço como ativo. Se era o ÚLTIMO, limpa a seleção — senão o
        // painel de detalhes continua exibindo os dados do serviço que acabou de ser
        // removido/arquivado (some da lista, mas fica "fantasma" no centro).
        if (this.orders().length > 0) this.setCurrentOrder(this.orders()[0]);
        else this.cleanCurrentOrder();

        // Arquivar/remover também tira o item do feed — revalida o cache
        this._feedMainService.refreshSilent();
    }

    /**
     * Atualiza a descrição do serviço atual
     * @param description Descrição do serviço (pode ser objeto Delta ou string)
     */
    public updateDescription(description: string): Observable<OrderEntity> {
        const orderId = this.currentOrder()?._id;
        const previous = this.currentOrder()?.description;

        // Atualiza o serviço atual
        this.currentOrder.set({
            ...this.currentOrder(),
            description: description,
        });

        // Pega o mesmo serviço na lista de serviços e atualiza
        this.orders.update(orders =>
            orders.map(order =>
                order._id === this.currentOrder()?._id
                    ? { ...order, description: description }
                    : order
            )
        );

        return this._orderController.update(this.currentOrder()).pipe(
            tap(() => this._feedMainService.refreshSilent()),
            // Falhou: desfaz a troca otimista, senão a tela mostra o que não foi salvo
            catchError(error => {
                this._patchOrder(orderId, { description: previous });
                return throwError(() => error);
            })
        );
    }

    /**
     * Aplica campos a um serviço no atual (se ainda for ele) e na lista
     * @param orderId ID do serviço
     * @param changes Campos a aplicar
     */
    private _patchOrder(orderId: string, changes: Partial<OrderEntity>): void {
        if (!orderId) return;

        if (this.currentOrder()?._id === orderId) {
            this.currentOrder.set({ ...this.currentOrder(), ...changes });
        }

        this.orders.update(orders =>
            orders.map(order => (order._id === orderId ? { ...order, ...changes } : order))
        );
    }

    /**
     * Atualiza o cliente do serviço atual
     * @param client Cliente
     */
    public updateClient(client: ClientEntity): Observable<OrderEntity> {
        const orderId = this.currentOrder()?._id;
        const previousClientId = this.currentOrder()?.clientId;

        this.currentOrder.set({
            ...this.currentOrder(),
            clientId: client._id,
        });

        return this._orderController.update(this.currentOrder()).pipe(
            map(order => {
                // Muda o cliente do serviço atual
                this.currentOrder.set({
                    ...this.currentOrder(),
                    client: client,
                });

                // Também muda esse valor na listagem de cache
                this.orders.update(orders =>
                    orders.map(order =>
                        order._id === this.currentOrder()?._id
                            ? { ...order, client: client }
                            : order
                    )
                );

                this._feedMainService.refreshSilent();

                return order;
            }),
            // Falhou: volta o cliente anterior (só o id foi trocado de forma otimista)
            catchError(error => {
                if (this.currentOrder()?._id === orderId) {
                    this.currentOrder.set({ ...this.currentOrder(), clientId: previousClientId });
                }
                return throwError(() => error);
            })
        );
    }

    /**
     * Atualiza os funcionários do serviço atual
     * @param employees IDs of employees
     */
    public updateEmployees(
        employeesToAdd: string[],
        employeesToRemove: string[]
    ): Observable<OrderEntity> {
        return this._orderController
            .updateEmployees(this.currentOrder()?._id, employeesToAdd, employeesToRemove)
            .pipe(
                map(order => {
                    this.currentOrder.set({
                        ...this.currentOrder(),
                        employees: order.employees,
                    });

                    return order;
                })
            );
    }

    public setOrder(order: OrderEntity): void {
        this.currentOrder.set(order);
        this._updateUrlWithOrderId(order?._id);
    }

    /**
     * Recarrega o serviço atual do servidor e substitui o `currentOrder` inteiro.
     * Fonte da verdade pós-mutação (ex.: auto-save de membros): garante que tudo
     * que deriva do signal (avatares da equipe, cliente, status) reflita o banco.
     */
    public refreshCurrentOrder(): Observable<OrderEntity> {
        const id = this.currentOrder()?._id;
        if (!id) return of(null);

        return this._orderController.getById(id).pipe(
            tap(order => {
                if (order) this.currentOrder.set(order);

                // O card correspondente no feed é cache — revalida junto para os
                // mini-avatares/contagens do item acompanharem a mutação.
                this._feedMainService.refreshSilent();
            })
        );
    }

    /** Solicita que a tela recarregue a lista de documentos do serviço atual. */
    public requestDocumentsReload(): void {
        this.documentsReloadTick.update(tick => tick + 1);

        // Documentos mudaram → contagem/última atividade do card no feed também
        this._feedMainService.refreshSilent();
    }

    /**
     * Atualiza a URL com o ID da order
     * @param orderId ID da order
     */
    private _updateUrlWithOrderId(orderId: string): void {
        if (orderId) {
            this._router.navigate([], {
                queryParams: { orderId },
                queryParamsHandling: 'merge',
            });
        }
    }
}
